"use client"

import {useCallback, useEffect, useState} from "react"
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card"
import {Button} from "@/components/ui/button"
import {Input} from "@/components/ui/input"
import {Label} from "@/components/ui/label"
import {Badge} from "@/components/ui/badge"
import { Image as ImageIcon, ArrowsClockwise as RefreshCw, MagnifyingGlass as Search, Trash, User } from "@phosphor-icons/react"
import {apiRequestWithAuth, getStoredToken, isTokenValid} from "@/lib/api-config"
import {useToast} from "@/hooks/use-toast"
import {useAuth} from "@/contexts/auth-context"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/components/ui/dialog"
import NotLoggedIn from "@/components/not-logged-in"

interface LogEntry {
  id: number
  level: string
  taskType: string
  title: string
  detail: string
  imageUrl: string
  from: string
  server: number
  name: string
  account: string
  password: null
  time: string
  delete: number
}

interface LogListResponse {
  code: number
  msg: string
  data: {
    current: number
    page: number
    total: number
    records: LogEntry[]
  }
}

export default function LogsPage() {
  const { token: contextToken } = useAuth()
  const { toast } = useToast()
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    size: 5,
    total: 0,
    page: 0,
  })

  const [searchAccount, setSearchAccount] = useState("")
  const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  useEffect(() => {
    if (searchParams) {
      const accountParam = searchParams.get("account")
      if (accountParam) {
        setSearchAccount(accountParam)
        setPagination((prev) => ({ ...prev, current: 1 }))
        fetchLogs(1, accountParam)
      }
    }
  }, [])

  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false)
  const [selectedImageUrl, setSelectedImageUrl] = useState("")

  const getToken = () => {
    return contextToken || getStoredToken()
  }

  const fetchLogs = useCallback(
    async (pageToFetch: number, accountKeyword = "") => {
      const token = getToken()
      if (!token || !isTokenValid(token)) {
        toast({
          variant: "destructive",
          title: "认证失败",
          description: "请重新登录",
        })
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        let endpoint: string
        const params = new URLSearchParams({
          current: pageToFetch.toString(),
          size: pagination.size.toString(),
        })
        if (accountKeyword.trim()) {
          endpoint = "/searchLogByAccount"
          params.append("account", accountKeyword.trim())
        } else {
          endpoint = "/showLog"
        }
        const result: LogListResponse = await apiRequestWithAuth(`${endpoint}?${params.toString()}`, token, {
          method: "GET",
        })
        if (result.code === 200) {
          setLogs(result.data.records)
          setPagination({
            current: result.data.current,
            size: pagination.size,
            total: result.data.total,
            page: result.data.page,
          })
        } else {
          toast({
            variant: "destructive",
            title: "获取日志列表失败",
            description: result.msg || "无法获取日志数据",
          })
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "网络错误",
          description: error instanceof Error ? error.message : "无法连接到服务器",
        })
      } finally {
        setLoading(false)
      }
    },
    [contextToken, pagination.size, toast],
  )

  useEffect(() => {
    const token = getToken()
    if (token && isTokenValid(token)) {
      fetchLogs(pagination.current, searchAccount)
    }
  }, [contextToken, fetchLogs, searchAccount])

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 })
    fetchLogs(1, searchAccount)
  }

  const handleReset = () => {
    setSearchAccount("")
    setPagination({ ...pagination, current: 1 })
    fetchLogs(1, "")
  }

  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, current: newPage })
    fetchLogs(newPage, searchAccount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("zh-CN")
  }

  const getTaskTypeName = (taskType: string) => {
    const taskTypes: Record<string, string> = {
      daily: "日常任务",
      rogue: "肉鸽任务",
      sand_fire: "生息演算",
    }
    return taskTypes[taskType] || taskType
  }

  const handleDetail = (log: LogEntry) => {
    setSelectedLog(log)
    setShowDetailDialog(true)
  }

  const handleDeleteLog = async (id: number) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({
        variant: "destructive",
        title: "认证失败",
        description: "请重新登录",
      })
      return
    }

    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/delLog", token, {
        method: "POST",
        body: JSON.stringify({ id }),
      })
      if (result.code === 200) {
        toast({
          variant: "success",
          title: "删除成功",
          description: `日志 ${id} 已被删除`,
        })
        await fetchLogs(pagination.current, searchAccount)
      } else {
        toast({
          variant: "destructive",
          title: "删除失败",
          description: result.msg || `无法删除日志 ${id}`,
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "操作失败",
      })
    } finally {
      setLoading(false)
    }
  }

  const token = getToken()
  if (!token || !isTokenValid(token)) {
    return (
      <NotLoggedIn bg="var(--ember-50)" accent="var(--ember-400)" text="var(--ember-600)" />
    )
  }

  return (
    <>
      <div className="mb-6">
        <div className="page-title-box">
          <div className="page-title">日志管理</div>
          <div className="page-subtitle">查看和管理系统运行日志</div>
        </div>
      </div>

      <Card style={{ border: "1.5px solid var(--ember-300)", background: "radial-gradient(circle, rgba(225,140,111,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(255,185,160,0.12) 1px, transparent 1px), var(--ember-50)", color: "var(--ember-600)" }} className="mb-6  border-ember-300">
        <details className="admin-log-search">
          <summary>搜索日志{searchAccount && <span> · {searchAccount}</span>}</summary>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="space-y-2">
              <Label htmlFor="accountSearch" className="text-ember-600">
                账户搜索
              </Label>
              <Input
                id="accountSearch"
                placeholder="输入账户名称"
                value={searchAccount}
                onChange={(e) => setSearchAccount(e.target.value)}
                className=" text-ember-600"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSearch} disabled={loading}>
              <Search className="mr-2 h-4 w-4" />
              搜索
            </Button>
            <Button onClick={handleReset} variant="outline" disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" />
              重置
            </Button>
          </div>
        </CardContent>
        </details>
      </Card>

      <Card style={{ border: "1.5px solid var(--ember-300)", background: "radial-gradient(circle, rgba(225,140,111,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(255,185,160,0.12) 1px, transparent 1px), var(--ember-50)", color: "var(--ember-600)" }} className=" border-ember-300">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-ember-600">
            <div className="flex items-center gap-2">
              <span>日志列表</span>
              <Badge variant="outline" className="border-ember-300 text-soil-600">
                共 {pagination.total} 条日志
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              <span className="text-ember-600">加载中...</span>
            </div>
          ) : logs.length > 0 ? (
            <div className="space-y-4">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="admin-log-card border rounded-lg p-4 hover:bg-gray-50 border-ember-300 hover:bg-ember-50"
                >
                  <div className="admin-log-row">
                    <div className="admin-log-info">
                      <div className="admin-log-heading flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg text-ember-600">{log.title}</h3>
                        <Badge variant={log.level === "ERROR" ? "destructive" : "default"}>{log.level}</Badge>
                        <Badge variant="secondary" className="admin-log-type">{getTaskTypeName(log.taskType)}</Badge>
                      </div>

                      <div className="admin-log-meta">
                        <div>
                          <span className="text-gray-500 text-soil-600">ID: </span>
                          <span className="text-ember-600">{log.id}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 text-soil-600">时间: </span>
                          <span className="text-ember-600">{formatDate(log.time)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 text-soil-600">账户名称: </span>
                          <span className="text-ember-600">{log.name}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 text-soil-600">账户: </span>
                          <span className="text-ember-600">{log.account}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 text-soil-600">来自设备: </span>
                          <span className="text-ember-600">{log.from}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 text-soil-600">服务器: </span>
                          <span className="text-ember-600">{log.server === 0 ? "官服" : "B服"}</span>
                        </div>
                      </div>

                      <div className="admin-log-preview">
                        <span className="text-gray-500 text-soil-600">详情: </span>
                        <span className="text-ember-600">{log.detail}</span>
                      </div>
                    </div>

                    <div className="admin-log-actions">
                      <Button className="admin-log-detail" size="sm" variant="outline" onClick={() => handleDetail(log)} disabled={loading}>
                        详情
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="destructive" disabled={loading} aria-label={`删除日志 ${log.id}`} title="删除日志">
                            <Trash className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="" style={{ background: "var(--ember-50)" }}>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-ember-600">确认删除日志？</AlertDialogTitle>
                            <AlertDialogDescription className="text-soil-600">
                              此操作将永久删除日志 ID: {log.id}。此操作不可撤销。
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-ember-300 text-ember-600">取消</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteLog(log.id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              删除
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}

              <div className="responsive-pagination flex items-center justify-between pt-4">
                <div className="text-sm text-gray-500 text-soil-600">
                  显示第 {(pagination.current - 1) * pagination.size + 1} -{" "}
                  {Math.min(pagination.current * pagination.size, pagination.total)} 条，共 {pagination.total} 条
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagination.current <= 1}
                    onClick={() => handlePageChange(pagination.current - 1)}
                  >
                    上一页
                  </Button>
                  <span aria-live="polite">第 {pagination.current} / {pagination.page} 页</span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagination.current >= pagination.page}
                    onClick={() => handlePageChange(pagination.current + 1)}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-soil-600">暂无日志数据</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto " style={{ background: "var(--ember-50)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-ember-600">
              <ImageIcon className="h-5 w-5" />
              日志详情 - {selectedLog?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 text-sm text-soil-600">
              <p>
                <span className="font-medium">ID:</span> {selectedLog.id}
              </p>
              <p>
                <span className="font-medium">级别:</span> {selectedLog.level}
              </p>
              <p>
                <span className="font-medium">任务类型:</span> {getTaskTypeName(selectedLog.taskType)}
              </p>
              <p>
                <span className="font-medium">标题:</span> {selectedLog.title}
              </p>
              <p>
                <span className="font-medium">详情:</span> {selectedLog.detail}
              </p>
              <p>
                <span className="font-medium">来自设备:</span> {selectedLog.from}
              </p>
              <p>
                <span className="font-medium">服务器:</span> {selectedLog.server === 0 ? "官服" : "B服"}
              </p>
              <p>
                <span className="font-medium">账户名称:</span> {selectedLog.name}
              </p>
              <p>
                <span className="font-medium">账户:</span> {selectedLog.account}
              </p>
              <p>
                <span className="font-medium">时间:</span> {formatDate(selectedLog.time)}
              </p>
              {selectedLog.imageUrl && (
                <div>
                  <p className="font-medium mb-2">图片:</p>
                  <div
                    className="cursor-pointer border rounded-lg overflow-hidden border-ember-300"
                    onClick={() => {
                      setSelectedImageUrl(selectedLog.imageUrl)
                      setIsImageDialogOpen(true)
                    }}
                  >
                    <img
                      src={selectedLog.imageUrl || "/placeholder.svg"}
                      alt="Log Detail"
                      className="w-full h-auto object-contain max-h-96"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isImageDialogOpen} onOpenChange={setIsImageDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 " style={{ background: "var(--ember-50)" }}>
          <img
            src={selectedImageUrl || "/placeholder.svg"}
            alt="Zoomed Log Image"
            className="w-full h-full object-contain"
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
